import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { Button } from "@commonsku/styles";
import { REPORT_TOOLTIP_TARGET_ID } from "../report/columns/config/types";
import { useReportContext } from "../report/report_context";
import { EReportTypes } from "../report/routes/report_types";
import { useHasCapabilities, useTooltipAttributes } from "../../hooks";
import { oauth } from "../../utils";
import ExportToAccountingPopup, { AccountingExportReport, EntityType, exportToAccounting } from "./ExportToAccountingPopup";

const REPORT_TYPE_TO_EXPORT_PARAM_MAP = {
  [EReportTypes.FinanceReceiveBills]: {
    entityType: "PURCHASE-ORDER",
    rowId: "purchase_order_id",
    label: 'purchase orders',
  },
  [EReportTypes.FinanceExportedBills]: {
    entityType: "BILL",
    rowId: "bill_id",
    label: 'bills',
  },
  [EReportTypes.FinanceExportedInvoices]: {
    entityType: "INVOICE",
    rowId: "order_id",
    label: 'invoices',
  },
};

const StyledButton = styled(Button)`
  white-space: pre;
`;

const MAXIMUM_EXPORTABLE_ROWS = 250;

export default function ExportAllButton() {
  const {
    reportConfig: { type, endpoint },
    getRawQuery,
    firstLoaded,
    loading,
    totalRows,
  } = useReportContext();
  const [executing, setExecuting] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [accountingExportReport, setAccountingExportReport] = useState<AccountingExportReport | null | false>(false);
  const hasExportReport = useHasCapabilities("EXPORT-REPORT");
  const { entityType, rowId, label } = REPORT_TYPE_TO_EXPORT_PARAM_MAP[type] ?? {};

  const handleClick = useCallback(async () => {
    try {
      setExecuting(true);
      setIds([]);
      setAccountingExportReport(null);
      const query = getRawQuery();
      query['max-results'] = MAXIMUM_EXPORTABLE_ROWS.toString();
      const response = await oauth("GET", endpoint, query);
      const ids = response.json.report.map(row => row[rowId]);
      setIds(ids);
      await exportToAccounting(entityType, "CSV", ids, true);
    } catch (e) {
      toast.error("Unable to export");
    } finally {
      setExecuting(false);
      setAccountingExportReport(false);
      setIds([]);
    }
  }, [endpoint, getRawQuery, type, entityType, rowId]);

  const handleClose = useCallback(
    () => {
      setAccountingExportReport(false);
      setExecuting(false);
    },
    []
  );

  const disabled = !firstLoaded || loading || executing || (totalRows ?? 0) > MAXIMUM_EXPORTABLE_ROWS;
  const tooltipAttributes = useTooltipAttributes(
    (totalRows ?? 0) > MAXIMUM_EXPORTABLE_ROWS ? `Unable to export more than ${MAXIMUM_EXPORTABLE_ROWS} ${label}.` : null,
    REPORT_TOOLTIP_TARGET_ID
  );

  if (!hasExportReport || !entityType) {
    return null;
  }

  return (
    <StyledButton secondary onClick={handleClick} disabled={disabled} {...tooltipAttributes}>
        {false !== accountingExportReport && <ExportToAccountingPopup
          accountingSoftware="CSV"
          entityType={entityType}
          ids={ids}
          onClose={handleClose}
          report={accountingExportReport}
        />}
      Export All To CSV
    </StyledButton>
  );
};
